import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import prettyBytes from 'pretty-bytes';
import { Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCustomers } from '@/features/admin-customers/api';

import { useDeleteUpload, useDownloadUrl, useUploads } from '../api';
import type { Upload, UploadListFilters, UploadStatus, UploadType } from '../types';

const PAGE_SIZE = 25;

type StatusFilter = UploadStatus | 'all';
type TypeFilter = UploadType | 'all';

interface Props {
  isAdmin: boolean;
  // When set, narrows the admin list to a single customer's uploads. The
  // Customer column auto-hides — redundant in a single-customer view.
  customerIdFilter?: string;
}

export function UploadsListTable({ isAdmin, customerIdFilter }: Props) {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [uploadType, setUploadType] = useState<TypeFilter>('all');
  const [offset, setOffset] = useState(0);

  const filters: UploadListFilters = {
    status: status === 'all' ? undefined : status,
    uploadType: uploadType === 'all' ? undefined : uploadType,
    limit: PAGE_SIZE,
    offset,
    customerId: customerIdFilter,
  };

  const uploads = useUploads(filters);
  // Admin-only customer enrichment — same pattern as IssueListTable. Skip
  // the request entirely when there's no Customer column to render.
  const showCustomerColumn = isAdmin && !customerIdFilter;
  const customers = useCustomers('', { enabled: showCustomerColumn });
  const customerNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (showCustomerColumn && customers.data) {
      for (const c of customers.data.customers) {
        map.set(c.id, c.name || c.companySlug);
      }
    }
    return map;
  }, [showCustomerColumn, customers.data]);

  const downloader = useDownloadUrl();
  const deleteUpload = useDeleteUpload();
  // The upload pending deletion confirmation, or null when the dialog is closed.
  const [deleteTarget, setDeleteTarget] = useState<Upload | null>(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUpload.mutateAsync(deleteTarget.id);
      toast.success('Upload deleted');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  // Actions column is always present now (Delete). +1 over the prior count.
  const colCount = showCustomerColumn ? 7 : 6;

  return (
    <div className="space-y-4" data-testid="upload-list-root">
      <div className="flex flex-wrap items-center gap-3">
        <FilterSelect
          label="Status"
          value={status}
          onChange={(v) => {
            setStatus(v as StatusFilter);
            setOffset(0);
          }}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'complete', label: 'Complete' },
            { value: 'pending', label: 'Pending' },
            { value: 'failed', label: 'Failed' },
            { value: 'expired', label: 'Expired' },
          ]}
        />
        <FilterSelect
          label="Type"
          value={uploadType}
          onChange={(v) => {
            setUploadType(v as TypeFilter);
            setOffset(0);
          }}
          options={[
            { value: 'all', label: 'All types' },
            { value: 'recording', label: 'Recording' },
            { value: 'syslog', label: 'Syslog' },
          ]}
        />
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Filename</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Size</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Uploaded</TableHead>
            {showCustomerColumn && <TableHead>Customer</TableHead>}
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {uploads.isLoading && <SkeletonRows colCount={colCount} />}

          {uploads.isError && (
            <TableRow>
              <TableCell colSpan={colCount} className="py-6 text-center">
                <div className="text-sm text-destructive">Couldn&apos;t load uploads.</div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => uploads.refetch()}
                >
                  Retry
                </Button>
              </TableCell>
            </TableRow>
          )}

          {uploads.isSuccess && (uploads.data?.items.length ?? 0) === 0 && (
            <TableRow>
              <TableCell colSpan={colCount} className="py-6 text-center">
                <div className="text-sm text-muted-foreground">No uploads yet.</div>
                <Button variant="link" asChild className="mt-1">
                  <Link to="/uploads/new">Upload your first file</Link>
                </Button>
              </TableCell>
            </TableRow>
          )}

          {uploads.isSuccess &&
            uploads.data?.items.map((upload) => (
              <UploadRow
                key={upload.id}
                upload={upload}
                isAdmin={showCustomerColumn}
                customerName={customerNameById.get(upload.customerId)}
                onDownload={async () => {
                  try {
                    const { url } = await downloader.fetchUrl(upload.id);
                    window.location.href = url;
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : 'Download failed');
                  }
                }}
                onDelete={() => setDeleteTarget(upload)}
              />
            ))}
        </TableBody>
      </Table>

      {uploads.isSuccess && (uploads.data?.total ?? 0) > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, uploads.data?.total ?? 0)} of{' '}
            {uploads.data?.total ?? 0}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE_SIZE >= (uploads.data?.total ?? 0)}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent data-testid="upload-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete upload?</DialogTitle>
            <DialogDescription>
              {deleteTarget
                ? `"${deleteTarget.originalFilename}" will be permanently removed from storage. This can't be undone.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmDelete()}
              disabled={deleteUpload.isPending}
              data-testid="upload-delete-confirm"
            >
              {deleteUpload.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">{label}:</span>
      <div className="w-40">
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function SkeletonRows({ colCount }: { colCount: number }) {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: colCount }).map((__, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function UploadRow({
  upload,
  isAdmin,
  customerName,
  onDownload,
  onDelete,
}: {
  upload: Upload;
  isAdmin: boolean;
  customerName?: string;
  onDownload: () => void;
  onDelete: () => void;
}) {
  const canDownload = upload.status === 'complete';
  // Already-expired uploads have no storage object left to remove.
  const canDelete = upload.status !== 'expired';
  return (
    <TableRow data-testid={`upload-row-${upload.id}`} data-upload-id={upload.id}>
      <TableCell className="font-medium">
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-primary"
          onClick={onDownload}
          disabled={!canDownload}
          data-testid={`upload-list-row-filename-${upload.id}`}
        >
          <Download className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          {upload.originalFilename}
        </Button>
      </TableCell>
      <TableCell>
        <TypeBadge type={upload.uploadType} />
      </TableCell>
      <TableCell className="text-sm">{prettyBytes(upload.sizeBytes)}</TableCell>
      <TableCell data-testid={`upload-list-row-status-${upload.id}`}>
        <StatusBadge status={upload.status} />
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {formatDistanceToNow(new Date(upload.uploadedAt), { addSuffix: true })}
      </TableCell>
      {isAdmin && (
        <TableCell className="text-sm">{customerName ?? upload.customerId.slice(0, 8)}</TableCell>
      )}
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={onDelete}
          disabled={!canDelete}
          aria-label={`Delete ${upload.originalFilename}`}
          data-testid={`upload-list-row-delete-${upload.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function TypeBadge({ type }: { type: UploadType }) {
  if (type === 'recording') {
    return (
      <Badge data-type="recording" className="bg-blue-100 text-blue-700">
        Recording
      </Badge>
    );
  }
  return (
    <Badge data-type="syslog" className="bg-purple-100 text-purple-700">
      Syslog
    </Badge>
  );
}

function StatusBadge({ status }: { status: UploadStatus }) {
  const colour: Record<UploadStatus, string> = {
    pending: 'bg-gray-100 text-gray-700',
    complete: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    expired: 'bg-gray-100 text-gray-500 line-through',
  };
  return (
    <Badge data-status={status} className={colour[status]}>
      {status}
    </Badge>
  );
}
